import { Clock, AlertTriangle } from "lucide-react"
import { useEffect, useState } from "react"

import { cn } from "@/lib/utils"

type TimeRemaining = {
  days: number
  hours: number
  minutes: number
  seconds: number
  totalMs: number
  isExpired: boolean
}

function calculateTimeRemaining(targetTime: number): TimeRemaining {
  const now = Date.now()
  const diff = targetTime - now

  if (diff <= 0) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      totalMs: 0,
      isExpired: true,
    }
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
  const seconds = Math.floor((diff % (1000 * 60)) / 1000)

  return {
    days,
    hours,
    minutes,
    seconds,
    totalMs: diff,
    isExpired: false,
  }
}

function formatTimeUnit(value: number, unit: string): string {
  return `${value}${unit}`
}

type CountdownTimerProps = {
  targetDate: Date
  label?: string
  expiredLabel?: string
  showIcon?: boolean
  urgentThresholdMs?: number
  className?: string
  compact?: boolean
}

export function CountdownTimer({
  targetDate,
  label = "Time remaining",
  expiredLabel = "Deadline passed",
  showIcon = true,
  urgentThresholdMs = 24 * 60 * 60 * 1000, // 24 hours
  className,
  compact = false,
}: CountdownTimerProps) {
  const time = useCountdown(targetDate)
  // const [time, setTime] = useState<TimeRemaining>(() =>
  //   calculateTimeRemaining(targetDate),
  // )
  //
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     setTime(calculateTimeRemaining(targetDate))
  //   }, 1000)
  //
  //   return () => clearInterval(interval)
  // }, [targetDate])

  const isUrgent = !time.isExpired && time.totalMs < urgentThresholdMs

  if (time.isExpired) {
    return (
      <div
        className={cn(
          "inline-flex items-center gap-1.5 rounded-lg bg-danger/10 px-3 py-1.5 text-sm font-medium text-danger",
          className,
        )}
        role="status"
        aria-live="polite"
      >
        {showIcon && <AlertTriangle className="size-4" aria-hidden="true" />}
        <span>{expiredLabel}</span>
      </div>
    )
  }

  const parts: string[] = []

  if (time.days > 0) {
    parts.push(formatTimeUnit(time.days, "d"))
  }
  if (time.hours > 0 || time.days > 0) {
    parts.push(formatTimeUnit(time.hours, "h"))
  }
  if (time.minutes > 0 || time.hours > 0 || time.days > 0) {
    parts.push(formatTimeUnit(time.minutes, "m"))
  }
  if (!compact || parts.length === 0) {
    parts.push(formatTimeUnit(time.seconds, "s"))
  }

  return (
    <div
      className={cn(
        "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
        isUrgent
          ? "bg-warning/10 text-warning-foreground"
          : "bg-muted text-muted-foreground",
        className,
      )}
      role="timer"
      aria-live="polite"
      aria-label={`${label}: ${parts.join(" ")}`}
    >
      {showIcon && (
        <Clock
          className={cn("size-4", isUrgent && "animate-pulse")}
          aria-hidden="true"
        />
      )}
      <span className="tabular-nums">{parts.join(" ")}</span>
    </div>
  )
}

export function useCountdown(targetDate: Date) {
  const targetTime = targetDate.getTime()

  const [time, setTime] = useState<TimeRemaining>(() =>
    calculateTimeRemaining(targetTime),
  )

  useEffect(() => {
    const update = () => {
      setTime(calculateTimeRemaining(targetTime))
    }

    update() // initial update

    const intervalId = setInterval(update, 1000)

    return () => clearInterval(intervalId)
  }, [targetTime])

  return time
}
